import React, { useCallback, useMemo, useState } from 'react';
import {
    REFRESH_CURRENT_DOSSIER_CTX,
    SelectFormality,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDomaine,
    useDossierRoutingContext,
    useOrigine,
} from '@europrocurement/l2d-modules';
import { Box, Typography, useTheme } from '@mui/material';
import { FaOptionIcon, informationIcon } from '@europrocurement/l2d-icons';
import { CatalogApiObject } from '@europrocurement/l2d-domain';
import { useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { faArrowLeft } from '@fortawesome/sharp-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { castIriToId } from '@europrocurement/l2d-utils';
import { useAddFormality } from '../../hooks';

const DossierAddFormality: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const { getDossierRoute } = useDossierRoutingContext();
    const navigate = useNavigate();
    const { addFormality } = useAddFormality();
    const domaineId = useDomaine();
    const origineId = useOrigine();
    const [processing, setProcessing] = useState<boolean>(false);
    const { modalActions } = useModal();
    const { palette } = useTheme();

    const handleAddFormality = async (catalogs: CatalogApiObject[]) => {
        if (!dossier) {
            return;
        }
        setProcessing(true);

        try {
            const newPrestations = await addFormality(dossier, catalogs, true);
            const dossierId = castIriToId(newPrestations[0].data.dossier);

            if (dossierId !== dossier.id) {
                modalActions.call(
                    <ConfirmationModal
                        messages={{
                            question: "Un nouveau dossier vient d'être créé.",
                            detail: {
                                message: 'Souhaitez vous ouvrir le nouveau dossier ?',
                                severity: 'warning',
                            },
                        }}
                        actionOnValidation={() => {
                            modalActions.close();
                            navigate(getDossierRoute(dossierId));
                        }}
                        actionOnCancellation={() => {
                            modalActions.close();
                            refreshDossier();
                            navigate(getDossierRoute(dossier.id as number));
                        }}
                        color={palette.primary}
                    />,
                );

                return;
            }

            refreshDossier();
            navigate(getDossierRoute(dossier.id as number));
        } catch (err) {
            enqueueSnackbar("Une erreur est survenue lors de l'ajout de votre formalité", {
                variant: 'error',
            });
            console.error(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleReturnToDossier = useMemo(
        () => (dossier ? () => navigate(getDossierRoute(dossier.id as number)) : () => {}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossier],
    );

    return (
        <>
            <Box
                onClick={handleReturnToDossier}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '16px',
                    color: 'text.gris50',
                    '&:hover': { cursor: 'pointer' },
                }}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
                <Typography>Revenir au dossier</Typography>
            </Box>
            <Typography
                component="h2"
                variant="l2dh4"
                sx={{
                    lineHeight: 1.4,
                }}
            >
                Quelle(s) formalité(s) souhaitez-vous réaliser ?
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    fontSize: '18px',
                    marginTop: '15px',
                    marginBottom: '15px',
                }}
            >
                <Typography
                    variant="l2dlabel"
                    color="text.secondary"
                >
                    <FaOptionIcon {...informationIcon.props} />
                </Typography>
                <Box
                    fontSize="14px"
                    marginLeft="8px"
                >
                    <Typography
                        variant="l2dlabel"
                        color="text.secondary"
                    >
                        Vous pouvez sélectionner plusieurs formalités à créer pour une même société.
                    </Typography>
                </Box>
            </Box>
            <Box>
                <SelectFormality
                    origine={origineId}
                    domaine={domaineId}
                    prescripteurId={dossier?.prescripteurId as number}
                    onChange={handleAddFormality}
                    disableSubmit={processing}
                />
            </Box>
        </>
    );
};

export default DossierAddFormality;
